import type { CSSProperties, PropsWithChildren } from 'react'
import { useMemo, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'

import { addMilliseconds } from 'date-fns'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { uniq as _uniq } from 'lodash'
import _indexOf from 'lodash/indexOf'

import type { Column } from '~shared/rundown/column'
import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import type { Rundown } from '~shared/rundown/rundown'
import { CueRunState } from '~shared/utils'

import { momentAtom } from '@/store/moment.store.ts'
import { timestampsAtom } from '@/store/timestamps.store.ts'

import { OutputCell } from '@/features/rundown-output/OutputCell.tsx'
import { CellContext } from '@/features/rundown/cell/cell.context'
import { makeCellKey } from '@/features/rundown/cell/cell.schema.ts'
import { CueContext } from '@/features/rundown/cue/cue.context'
import { CueElapsed } from '@/features/rundown/cue/CueElapsed'
import {
  useGroupTiming,
  useGroupTimestamps,
} from '@/features/rundown/cue/group/group.hooks'
import { GroupPlannedTimeRange } from '@/features/rundown/cue/group/GroupPlannedTimeRange'
import { useRundown } from '@/features/rundown/rundown.derived'

import formatTimeOfDay from '../../utils/formatTimeOfDay.js'
import { extractTextNewLines } from '../../utils/index.js'
import { formatDurationHuman } from '../../utils/index.ts'
import { useColumnsDerived } from '../rundown/column/column.derived.ts'
import { useCuesDerived } from '../rundown/cue/cue.derived.ts'
import { useCuesById } from '../rundown/data/cues.store.ts'

import * as OUTPUT_TYPES from './rundown-output.const.ts'
import { RundownOutputCueSubTimers } from './RundownOutputSubTimers'
import { useRundownOutputOptions } from './useRundownOutputOptions.ts'

const ScrollIntoView = lazy(
  () => import('@/components/rundown/ScrollIntoView.jsx'),
)

// Minimum width (rem) for the left block (index / times / title) so it never
// crushes on narrow screens. Sized to hold the index, the 120px time column and
// a readable slice of the title. Above this it stays a fluid ~1/3 of the row,
// matching the original wide-screen layout (see buildGridStyle).
const LEFT_COL_MIN_REM = 22
// Minimum readable width (rem) for each cell column. When the visible columns
// can't all fit at this width the body scrolls horizontally rather than
// crushing the text to one character per line on small screens.
const CELL_COL_MIN_REM = 9
// Matches the `gap-4` (1rem) gap between grid tracks.
const GRID_GAP_REM = 1

/**
 * Build the grid style shared by the header and every row so the cell columns
 * stay aligned while the whole body scrolls horizontally as one unit.
 *
 * `gridTemplateColumns` reproduces the original proportions: the left block
 * takes ~1/3 of the row and the cell columns share the remaining ~2/3 evenly
 * (left track of `columnCount / 2` fr against `1fr` cells keeps that 1:2 ratio
 * for any column count).
 *
 * `minWidth` is the explicit sum of every track's minimum plus the gaps, so on
 * narrow screens the row grows to keep each track at its readable minimum and
 * the body scrolls horizontally — instead of the `1fr` tracks collapsing below
 * their `minmax` floor (which happens when overflow-hidden cell content reports
 * no min-content width). On wide screens minWidth is below the viewport, so the
 * `fr` tracks expand to fill and no scrollbar appears.
 */
function buildGridStyle(columnCount: number): CSSProperties {
  if (columnCount === 0) {
    return {
      gridTemplateColumns: `minmax(${LEFT_COL_MIN_REM}rem, 1fr)`,
      minWidth: `${LEFT_COL_MIN_REM}rem`,
    }
  }
  const leftFr = columnCount / 2
  const minWidthRem =
    LEFT_COL_MIN_REM +
    columnCount * CELL_COL_MIN_REM +
    columnCount * GRID_GAP_REM
  return {
    gridTemplateColumns: `minmax(${LEFT_COL_MIN_REM}rem, ${leftFr}fr) repeat(${columnCount}, minmax(${CELL_COL_MIN_REM}rem, 1fr))`,
    minWidth: `${minWidthRem}rem`,
  }
}

export function RundownOutputLayoutList() {
  const rundown = useRundown()
  const cues = useCuesById()
  const { sortedColumns } = useColumnsDerived()

  const [searchParams] = useSearchParams()

  const visibleColumnsSearchParam = searchParams.get(OUTPUT_TYPES.COLUMNS) || ''

  const visibleColumnIds = useMemo(
    () => _uniq(visibleColumnsSearchParam.split(',').filter(Boolean)),
    [visibleColumnsSearchParam],
  )
  const visibleColumns = useMemo(
    () =>
      sortedColumns.filter((column) => visibleColumnIds.includes(column.id)),
    [visibleColumnIds, sortedColumns],
  )

  const { indexedCuesWithoutSkipped: indexedCues } = useCuesDerived()

  return (
    <div className="p-2 flex flex-col gap-4 mb-[90vh]">
      {/* Column titles */}
      <div
        className="sticky top-0 grid gap-4 z-50 bg-black"
        style={buildGridStyle(visibleColumns.length)}
      >
        {/* Spacer aligning with the left block of each row */}
        <div></div>
        {visibleColumns.map(({ name }) => (
          <div
            key={name}
            className="border-b-2 border-white/20"
          >
            {name}
          </div>
        ))}
      </div>

      {indexedCues.map((indexedCue) => {
        const cue = cues[indexedCue.id]
        if (!cue) return null

        // Skip group children — they're rendered inside CueItemGroup
        if (indexedCue.parentId) return null

        if (cue.type === CueType.HEADING) {
          return (
            <CueItemHeading
              key={indexedCue.id}
              cue={cue}
            />
          )
        }

        if (cue.type === CueType.GROUP) {
          return (
            <CueContext.Provider
              key={indexedCue.id}
              value={cue.id}
            >
              <CueItemGroup
                index={indexedCue.displayIndex}
                cue={cue}
                groupChildren={indexedCue.children}
                cues={cues}
                timezone={rundown.timezone}
                visibleColumns={visibleColumns}
                todDisplayFormat={rundown.settings.todDisplayFormat}
              />
            </CueContext.Provider>
          )
        }

        return (
          <CueContext.Provider
            key={indexedCue.id}
            value={cue.id}
          >
            <CueItemCue
              index={indexedCue.displayIndex}
              timestampId={indexedCue.id}
              cue={cue}
              timezone={rundown.timezone}
              visibleColumns={visibleColumns}
              todDisplayFormat={rundown.settings.todDisplayFormat}
            />
          </CueContext.Provider>
        )
      })}
    </div>
  )
}

type CueItemGroupProps = {
  index: string | number
  cue: Cue
  // Ids only, readonly: fed from a valtio snapshot of the indexed cue list.
  groupChildren: readonly { id: string }[] | undefined
  cues: Record<string, Cue>
  timezone: Rundown['timezone']
  visibleColumns: Column[]
  todDisplayFormat: Rundown['settings']['todDisplayFormat']
}

function CueItemGroup({
  index,
  cue,
  groupChildren,
  cues,
  timezone,
  visibleColumns,
  todDisplayFormat,
}: CueItemGroupProps) {
  const { getOption } = useRundownOutputOptions()

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) !== OUTPUT_TYPES.OFF
      ? cue.backgroundColor
      : 'transparent'

  let displayChildIndex = 0

  return (
    <div className="w-full space-y-4">
      <div
        className="min-h-20 p-3 bg-gray-950 rounded-sm flex items-center gap-8 text-2xl"
        style={{
          backgroundColor: cueBackgroundColor || 'transparent',
        }}
      >
        <CueIndex>{index}</CueIndex>
        <div className="flex min-w-0 grow flex-col gap-1 wrap-break-word">
          {/* Cue title */}
          {getOption(OUTPUT_TYPES.TITLES) !== OUTPUT_TYPES.OFF && (
            <p>{cue.title}</p>
          )}

          {/* Cue timing */}
          <CueItemGroupTiming
            timezone={timezone}
            groupChildren={groupChildren}
            todDisplayFormat={todDisplayFormat}
          />
        </div>
      </div>
      {Object.values(groupChildren ?? {}).map((_cue) => {
        const cue = cues[_cue.id]

        if (!cue) return null

        if (cue.type === CueType.HEADING)
          return (
            <CueItemHeading
              key={index}
              cue={cue}
            />
          )

        // Skip individually skipped children within a visible group
        if (cue.settings?.skipDuringShow) return null

        displayChildIndex++

        return (
          <CueItemCue
            key={`${index}_child_${displayChildIndex}`}
            index={`${index}.${displayChildIndex}`}
            timestampId={_cue.id}
            cue={cue}
            timezone={timezone}
            visibleColumns={visibleColumns}
            todDisplayFormat={todDisplayFormat}
          />
        )
      })}
    </div>
  )
}

function CueItemGroupTiming({
  timezone,
  groupChildren,
  todDisplayFormat,
}: Pick<CueItemGroupProps, 'timezone' | 'groupChildren' | 'todDisplayFormat'>) {
  const groupTimestamps = useGroupTimestamps(groupChildren || [])
  const groupTiming = useGroupTiming(groupTimestamps)

  return (
    <GroupPlannedTimeRange
      groupTiming={groupTiming}
      className="text-sm! font-mono"
      timezone={timezone}
      todDisplayFormat={todDisplayFormat}
    />
  )
}

function CueItemHeading({ cue }: { cue: Cue }) {
  const { getOption } = useRundownOutputOptions()

  // A heading is nothing but its title, so drop the whole row rather than
  // leaving an empty coloured bar behind.
  if (getOption(OUTPUT_TYPES.TITLES) === OUTPUT_TYPES.OFF) return null

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) !== OUTPUT_TYPES.OFF
      ? cue.backgroundColor
      : 'transparent'

  return (
    <div
      className="w-full px-3 py-1 rounded-sm flex items-center gap-8 text-2xl"
      style={{
        backgroundColor: cueBackgroundColor || 'transparent',
      }}
    >
      <div className="min-w-0 grow wrap-break-word">
        <p>{cue.title}</p>
      </div>
    </div>
  )
}

function CueIndex({ children }: PropsWithChildren) {
  const { getOption } = useRundownOutputOptions()

  if (getOption(OUTPUT_TYPES.CUE_NUMBERS) === OUTPUT_TYPES.OFF) return null

  return <p className="shrink-0 whitespace-nowrap min-w-[2ch]">{children}</p>
}

type CueItemCueProps = {
  index: string | number
  timestampId: string
  cue: Cue
  timezone: Rundown['timezone']
  visibleColumns: Column[]
  todDisplayFormat: Rundown['settings']['todDisplayFormat']
}

function CueItemCue({
  index,
  timestampId,
  cue,
  timezone,
  visibleColumns,
  todDisplayFormat,
}: CueItemCueProps) {
  /** Cached atom access that only re-renders when specific state changes */
  const timestampStateAtom = useMemo(
    () =>
      selectAtom(timestampsAtom, (atoms) => atoms?.cues?.[timestampId]?.state),
    [timestampId],
  )

  const timestampState = useAtomValue(timestampStateAtom)

  const { getOption } = useRundownOutputOptions()

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) !== OUTPUT_TYPES.OFF
      ? cue.backgroundColor
      : 'transparent'

  const isTypeCue = useMemo(() => cue.type === CueType.CUE, [cue.type])
  const isActive = useMemo(
    () => isTypeCue && timestampState === CueRunState.CUE_ACTIVE,
    [isTypeCue, timestampState],
  )
  const cueStyle = useMemo(
    () =>
      isTypeCue
        ? {
            transition: 'ease 0.3s background-color',
            backgroundColor: isActive
              ? '#ef4444'
              : !cueBackgroundColor || cueBackgroundColor === 'transparent'
                ? '#161616'
                : cueBackgroundColor,
            borderRadius: '0.25rem',
          }
        : {
            transition: 'ease 0.3s background-color',
            backgroundColor:
              !cueBackgroundColor || cueBackgroundColor === 'transparent'
                ? 'transparent'
                : cueBackgroundColor,
            borderRadius: '0.25rem',
          },
    [isTypeCue, isActive, cueBackgroundColor],
  )

  if (!timestampState) return null

  return (
    <div
      className="relative rounded-sm grid gap-4 text-2xl"
      style={buildGridStyle(visibleColumns.length)}
    >
      {isActive && (
        <Suspense>
          <ScrollIntoView
            following
            marginTop={visibleColumns.length > 0 ? '-30px' : '0'}
          />
        </Suspense>
      )}
      <div
        className="flex items-center gap-8 p-3 w-full"
        style={cueStyle}
      >
        {/* Index */}
        <CueIndex>{index}</CueIndex>

        {/* Timing */}
        <CueItemTiming
          timestampId={timestampId}
          timezone={timezone}
          todDisplayFormat={todDisplayFormat}
          isActive={isActive}
          subTimers={cue.subTimers}
        />

        {/* Cue title + subtitle. The wrapper stays even when both are hidden so
            it keeps absorbing the leftover width of the left grid track. */}
        <div className="min-w-0 grow wrap-break-word">
          {getOption(OUTPUT_TYPES.TITLES) !== OUTPUT_TYPES.OFF && (
            <p>{extractTextNewLines(cue.title.trim())}</p>
          )}
          {getOption(OUTPUT_TYPES.SUBTITLES) !== OUTPUT_TYPES.OFF && (
            <p className="text-sm opacity-80">
              {extractTextNewLines(cue.subtitle.trim())}
            </p>
          )}
        </div>
      </div>
      {/* Cue cells */}
      {visibleColumns.map((column) => {
        const cellKey = makeCellKey(cue.id, column.id)

        return (
          <CellContext.Provider
            key={cellKey}
            value={cellKey}
          >
            <OutputCell style={cueStyle} />
          </CellContext.Provider>
        )
      })}
    </div>
  )
}

function CueItemTiming({
  timestampId,
  timezone,
  todDisplayFormat,
  isActive,
  subTimers,
}: Pick<CueItemCueProps, 'timezone' | 'todDisplayFormat'> & {
  timestampId: string
  isActive: boolean
  subTimers: Cue['subTimers']
}) {
  const moment = useAtomValue(momentAtom)
  /** Cached atom access that only re-renders when specific state changes */
  const timestampActualAtom = useMemo(
    () =>
      selectAtom(timestampsAtom, (atoms) => atoms?.cues?.[timestampId]?.actual),
    [timestampId],
  )

  const timestampActual = useAtomValue(timestampActualAtom)

  const { getOption } = useRundownOutputOptions()

  if (!timestampActual) return null

  // `start-times` / `end-times` default to off, so only an explicit `on`
  // turns them on. See the rule on getOption.
  const showStartTimes = getOption(OUTPUT_TYPES.START_TIMES) === OUTPUT_TYPES.ON
  const showEndTimes = getOption(OUTPUT_TYPES.END_TIMES) === OUTPUT_TYPES.ON
  // Covers both the static planned duration and the running timer on the
  // active cue, so `durations=off` clears the duration column completely.
  const showDuration = getOption(OUTPUT_TYPES.DURATIONS) !== OUTPUT_TYPES.OFF

  // Collapse the fixed-width timing gutter entirely when every part is hidden,
  // rather than leaving an empty 7.5rem column pushing the title across.
  const showSubTimers = subTimers.some((timer) => timer.showOnOutput)

  if (!showStartTimes && !showEndTimes && !showDuration && !showSubTimers)
    return null

  return (
    <div className="flex flex-col gap-2 w-30 min-w-30">
      {showStartTimes && (
        <p className="whitespace-nowrap font-mono text-left text-sm opacity-60 leading-3">
          {formatTimeOfDay(timestampActual.start, {
            timezone,
            seconds: 'nonzero',
            format: todDisplayFormat,
          })}
        </p>
      )}
      {showDuration && (
        <div className="font-mono">
          {isActive ? (
            <CueElapsed
              moment={moment}
              className="px-0!"
              overtimeClassName="px-1!"
            />
          ) : (
            formatDurationHuman(timestampActual.duration)
          )}
        </div>
      )}
      {showSubTimers && (
        <RundownOutputCueSubTimers
          cueId={timestampId}
          subTimers={subTimers}
        />
      )}
      {showEndTimes && (
        <p className="whitespace-nowrap font-mono text-left text-sm opacity-60 leading-3">
          {formatTimeOfDay(
            addMilliseconds(timestampActual.start, timestampActual.duration),
            {
              timezone,
              seconds: 'nonzero',
              format: todDisplayFormat,
            },
          )}
        </p>
      )}
    </div>
  )
}
